import { IdentitySource } from "@/@types/divergence";
import {
  ConciliationCaseRelationEnum,
  ConciliationChangeFieldEnum,
  ConciliationItemEventEnum,
} from "@/constants/conciliation-history";
import { DECISION_TRACKS, DecisionTrack } from "@/constants/decision-track";

export interface ToastConfig {
  variant?: "default" | "destructive" | "success" | "alert"
  title?: string
  description: string
  duration?: number
}

export type DecisionAction = 'approve' | 'reject'

interface BatchActionMessages {
  SUCCESS: (count: number, action: DecisionAction) => ToastConfig
  PARTIAL: (
    count: number,
    skippedSummary: string,
    action: DecisionAction
  ) => ToastConfig
  ERROR: (action: DecisionAction) => ToastConfig
  NO_SELECTION: ToastConfig
  NO_DECIDABLE_SELECTION: ToastConfig
  NO_REVERSIBLE_SELECTION: ToastConfig
}

interface ManageLossMessages {
  BATCH_ACTION: BatchActionMessages
}

const ITEM_NOUN = { SINGULAR: 'item', PLURAL: 'itens' } as const

const decidedItems = (count: number, action: DecisionAction): string => {
  const noun: string = count === 1 ? ITEM_NOUN.SINGULAR : ITEM_NOUN.PLURAL
  const participle: string =
    count === 1
      ? DECISION_VOCABULARY[action].participleSingular
      : DECISION_VOCABULARY[action].participlePlural

  return `${count} ${noun} ${participle}`
}

export const MANAGE_LOSS_MESSAGES: ManageLossMessages = {
  BATCH_ACTION: {
    SUCCESS: (count: number, action: DecisionAction) => ({
      description: `${decidedItems(count, action)} com sucesso!`,
      variant: "success",
      duration: 3000
    }),
    PARTIAL: (
      count: number,
      skippedSummary: string,
      action: DecisionAction
    ) => ({
      description: `${decidedItems(count, action)}. Ficaram de fora: ${skippedSummary}.`,
      variant: "alert",
      duration: 8000
    }),
    ERROR: (action: DecisionAction) => ({
      description: `Erro ao ${DECISION_VOCABULARY[action].infinitive} itens selecionados.`,
      variant: "destructive",
      duration: 3000
    }),
    NO_SELECTION: {
      description: "Selecione ao menos uma solicitação para continuar.",
      variant: "destructive",
      duration: 3000
    },
    NO_DECIDABLE_SELECTION: {
      description:
        "Nenhum dos casos selecionados está aguardando decisão. Aprovar e reprovar só valem para quem ainda não foi decidido.",
      variant: "destructive",
      duration: 5000
    },
    NO_REVERSIBLE_SELECTION: {
      description:
        "Nenhum dos casos selecionados pode ser revertido. Só um caso aprovado e ainda não revertido tem lançamento a desfazer.",
      variant: "destructive",
      duration: 5000
    }
  }
}

export const FOREIGN_DECISION_COUNT_TOAST = (count: number): ToastConfig => ({
  description:
    count === 1
      ? "Um caso saiu da sua lista: outro operador decidiu enquanto você olhava."
      : `${count} casos saíram da sua lista: outro operador decidiu enquanto você olhava.`,
  variant: "alert",
  duration: 5000,
});

const CASE_EVENT_LABELS: Readonly<Record<ConciliationItemEventEnum, string>> = {
  [ConciliationItemEventEnum.CREATED]: "Linha criada",
  [ConciliationItemEventEnum.FIELD_CHANGED]: "Alteração",
  [ConciliationItemEventEnum.SUBMITTED_FOR_APPROVAL]: "Enviado para aprovação",
  [ConciliationItemEventEnum.APPROVED]: "Aprovado",
  [ConciliationItemEventEnum.REJECTED]: "Reprovado",
  [ConciliationItemEventEnum.APPROVAL_INVALIDATED]: "Aprovação invalidada",
  [ConciliationItemEventEnum.VOUCHER_CREATED]: "Vale criado e enviado ao RH",
  [ConciliationItemEventEnum.VOUCHER_CANCELLED]: "Vale cancelado",
  [ConciliationItemEventEnum.VOUCHER_RELEASED]: "Vale liberado ao RH",
  [ConciliationItemEventEnum.VOUCHER_AUTO_APPROVED]: "Vale aprovado automaticamente",
  [ConciliationItemEventEnum.VOUCHER_DECIDED]: "Decisão do RH",
  [ConciliationItemEventEnum.CONCILIATED]: "Conciliado entre caixas",
  [ConciliationItemEventEnum.UNCONCILIATED]: "Saiu da conciliação entre caixas",
  [ConciliationItemEventEnum.REVERSED]: "Reversão",
  [ConciliationItemEventEnum.STORE_RESPONDED]: "Loja respondeu",
  [ConciliationItemEventEnum.DELETED]: "Linha removida",
};

const CASE_RELATION_LABELS: Readonly<Record<ConciliationCaseRelationEnum, string>> = {
  [ConciliationCaseRelationEnum.SELF]: "Esta linha",
  [ConciliationCaseRelationEnum.GROUP]: "Mesmo grupo",
  [ConciliationCaseRelationEnum.DERIVED]: "Derivada",
  [ConciliationCaseRelationEnum.ORIGIN]: "Origem",
};

export const CASE_TIMELINE_LABELS = {
  TITLE: "Histórico",
  DESCRIPTION: "Tudo o que aconteceu com este caso, da linha mais recente para a mais antiga",
  EMPTY: "Nada aconteceu com este caso ainda.",
  LOAD_ERROR:
    "Não foi possível carregar o histórico deste caso. Tente novamente em instantes.",
  EVENT: CASE_EVENT_LABELS,
  REASON: "Motivo:",
  SYSTEM_ACTOR: "Sistema",
  UNKNOWN_ACTOR: "Autor não registrado",
  RECONSTRUCTED:
    "Aprovado antes do registro de histórico: data aproximada, autor não registrado",
  APPROVALS_TITLE: "Aprovações",
  APPROVAL_VALID: "Válida",
  APPROVAL_INVALIDATED: "Invalidada",
  APPROVAL_DECIDED: "Aprovado",
  APPROVAL_DECIDED_BY: "Aprovado por",
  APPROVAL_VOUCHER: "Vale",
  CONCILIATION_TITLE: "Conciliação entre caixas",
  CONCILIATION_WITH: "Conciliada com",
  CONCILIATION_BALANCE: "Saldo",
  CONCILIATION_DERIVED: "Disso nasceu",
  CONCILIATION_OPEN_DERIVED: "Ver o histórico dessa linha",
  BACK_TO_ORIGIN: "Voltar ao histórico da linha aberta",
  LOADING: "Carregando o histórico",
  RELATED_TITLE: "Nesta conciliação",
  RELATED_OPEN: (itemId: number): string => `Ver o histórico da linha ${itemId}`,
  RELATED_COLUMNS: {
    ID: "#",
    REGISTER: "Caixa",
    DATE: "Data",
    AMOUNT: "Valor",
    STATUS: "Status",
    RELATION: "Relação",
  },
  RELATION: CASE_RELATION_LABELS,
} as const;

export const NSU_ROW_ACTION_LABELS = {
  OPEN_MENU: "Abrir Menu",
  DECIDE: "Aprovar/Rejeitar",
  ALREADY_DECIDED: "Status já definido",
  HISTORY: "Histórico",
  STATUS_MODAL_TITLE: "Atualizar Status do NSU",
  STATUS_MODAL_DESCRIPTION: "Atualize o status do NSU",
} as const;

export const CHANGE_BOOLEAN_LABELS: Record<string, string> = {
  true: "Sim",
  false: "Não",
};

export const CHANGE_EMPTY_VALUE_LABELS: Readonly<
  Partial<Record<ConciliationChangeFieldEnum, string>>
> = {
  [ConciliationChangeFieldEnum.JUSTIFICATION_ID]: "sem justificativa",
  [ConciliationChangeFieldEnum.CONCILIATED_DIVERGENCE_ID]: "sem grupo",
};

export const CHANGE_FIELD_LABELS: Readonly<Record<ConciliationChangeFieldEnum, string>> = {
  [ConciliationChangeFieldEnum.JUSTIFICATION_ID]: "Justificativa",
  [ConciliationChangeFieldEnum.IS_LOSS]: "Contabiliza perda",
  [ConciliationChangeFieldEnum.IS_PAYROLL_DISCOUNT]: "Desconto em folha",
  [ConciliationChangeFieldEnum.ADJUSTMENT_AMOUNT]: "Ajuste",
  [ConciliationChangeFieldEnum.AMOUNT]: "Valor",
  [ConciliationChangeFieldEnum.STATUS]: "Status",
  [ConciliationChangeFieldEnum.OPERATOR]: "Operador",
  [ConciliationChangeFieldEnum.REGISTER_CODE]: "Caixa",
  [ConciliationChangeFieldEnum.CONCILIATED_DIVERGENCE_ID]: "Grupo de conciliação",
};

export const TEXT_SEPARATORS = {
  WORD: " ",
  INLINE: " · ",
  TRANSITION: " → ",
  DETAIL: " — ",
  LIST: ", ",
  CHANGES: "; ",
  LABEL: ": ",
} as const;

export const ITEM_ID_PREFIX: string = "#";

export const PAYROLL_VOUCHER_STATUS_FILTER = {
  NONE: "NONE",
  NONE_LABEL: "Não enviado",
} as const;

export const NOT_AVAILABLE = "—";

export const BATCH_PREFLIGHT_LABELS = {
  WILL_PASS: "Serão processados",
  WILL_SKIP: "Serão pulados",
  TO_PAYROLL: (count: number): string =>
    count === 1
      ? "1 caso irá para o RH e o colaborador será descontado no salário."
      : `${count} casos irão para o RH e os colaboradores serão descontados no salário.`,
  NONE_TO_PAYROLL: "Nenhum caso desta seleção vai para o RH.",
  NOTHING_TO_PROCESS: "Nenhum item da seleção pode ser processado.",
  UNKNOWN_SKIP_REASON: "não puderam ser processados",
  TOTAL: "Total",
  ITEM: "item",
  ITEMS: "itens",
  STORE: "Loja:",
  OPERATOR: "Operador:",
  DATE: "Data:",
  DIVERGENCE: "Divergência",
  CONFIRM_TITLE: (action: string): string => `Confirmar ${action} em Lote`,
  INTRO: (action: string, count: number, typeLabel: string): string =>
    `Você está prestes a ${action.toLowerCase()} ${count} ${typeLabel}.`,
  SKIP_REASON: {
    NOT_FOUND: "Não encontrado",
    CLOSED_PERIOD: "Período fechado — apenas reversão é permitida",
    ALREADY_DECIDED: "Já decidido",
    NOT_IN_QUEUE: "Não é Perda nem Desconto em Folha",
    OPERATOR_UNRESOLVED: "Não foi possível identificar o colaborador do desconto",
    NON_NEGATIVE_AMOUNT: "Sem valor a descontar — desconto em folha exige falta de caixa",
    INSTALLMENTS_REQUIRED: "Sem quantidade de parcelas definida",
  },
} as const;

export const MANUAL_IDENTITY_LABELS = {
  TITLE: "Informar o CPF do colaborador",
  INTRO:
    "O sistema não conseguiu identificar de quem descontar. Informe o CPF e confira os dados antes de aprovar.",
  CANDIDATES_TITLE: "Encontrados no caixa do gerente desta conciliação",
  CANDIDATES_HINT:
    "Como há mais de um, o sistema não escolhe sozinho. Selecione quem deve receber o desconto.",
  CANDIDATE_OPERATOR: "Operador",
  CANDIDATE_NAME: "Nome",
  CPF_LABEL: "CPF do colaborador",
  CPF_PLACEHOLDER: "Somente números",
  CPF_HINT: (faltam: number): string =>
    `Informe os 11 dígitos do CPF — ${faltam === 1 ? "falta 1 dígito" : `faltam ${faltam} dígitos`}.`,
  SEARCH: "Consultar",
  SEARCHING: "Consultando...",
  CONFIRM: "Aprovar com este CPF",
  CONFIRM_CANDIDATE: "Aprovar com o selecionado",
  CANCEL: "Cancelar",
  FIELD: {
    CPF: "CPF",
    NAME: "Nome",
    REGISTRATION: "Matrícula",
    POSITION: "Cargo",
    SITUATION: "Situação",
    COST_CENTER: "Centro de custo",
    COMPANY: "Empresa",
    ADMISSION: "Admissão",
    RESIGNATION: "Demissão",
  },
  EMPTY_FIELD: "—",
  NOTICE: {
    TERMINATED_BEFORE:
      "Este colaborador já estava desligado na data da diferença. Confira se é a pessoa certa antes de aprovar.",
    NOT_YET_ADMITTED:
      "Este colaborador ainda não havia sido admitido na data da diferença. Confira se é a pessoa certa antes de aprovar.",
    UNKNOWN:
      "O cadastro não informa admissão nem demissão, então não foi possível confirmar o vínculo na data da diferença.",
  },
  ACKNOWLEDGE:
    "Li o alerta acima e confirmo que este é o colaborador correto.",
  RESPONSIBILITY:
    "Confira os dados acima. Ao aprovar, o desconto será vinculado a este CPF.",
  KNOWN_OPERATOR: (name: string, code: string): string =>
    code
      ? `O sistema identificou ${name || "o colaborador"} (operador ${code}).`
      : `O sistema identificou ${name || "o colaborador"}.`,
  REPLACEMENT_INTRO:
    "Informe o CPF do colaborador que deve receber o desconto no lugar do responsável identificado.",
} as const;

export const IDENTITY_RULE_ACK: Record<IdentitySource, string | null> = {
  OPERATOR:
    "Confirmo que esta loja paga quebra de caixa e o desconto deve ir para o operador da divergência.",
  MANAGER:
    "Confirmo que esta loja não paga quebra de caixa e o desconto deve ir para o gerente que operou o caixa 99.",
  INFORMED: null,
};

export const RESPONSIBLE_CONFIRM_LABELS = {
  INTRO:
    "O sistema identificou este colaborador a partir do caixa da conciliação. Confira antes de aprovar.",
  REPLACE: "Não é este colaborador",
  LOADING: "Consultando o cadastro...",
} as const;

export const DECISION_REASON_MAX_LENGTH = 500;

export const DECISION_REASON_MIN_LENGTH = 10;

export const DECISION_LABELS = {
  APPROVE: "Aprovar",
  REJECT: "Reprovar",
  HISTORY: "Histórico",
  ALREADY_DECIDED: "Status já definido",
  CLOSED_PERIOD: "Período fechado — apenas reversão é permitida",
  CLOSED_PERIOD_NO_ACTION:
    "Período fechado — este caso não aceita mais alteração",
  MENU: "Abrir Menu",
  TITLE_APPROVE: "Confirmar aprovação",
  TITLE_REJECT: "Confirmar reprovação",
  DESCRIPTION_APPROVE: "Confira o colaborador e a consequência antes de confirmar",
  DESCRIPTION_REJECT: "O caso volta para o Analista",
  CONSEQUENCE_PAYROLL_DISCOUNT:
    "Este caso vai para a fila do RH e o colaborador será descontado no salário.",
  INSTALLMENTS_LABEL: "Quantidade de parcelas",
  INSTALLMENTS_HINT:
    "Você define o parcelamento; o RH aprova ou recusa esta quantidade. Se recusar, o caso volta para você reparcelar.",
  INSTALLMENTS_PREVIEW: "Cada parcela ficará em",
  INSTALLMENTS_BATCH_APPLY_ALL: "Aplicar a todos",
  INSTALLMENTS_BATCH_HINT:
    "Defina a quantidade de parcelas de cada caso de Desconto em Folha.",
  CONSEQUENCE_ACCOUNTED_LOSS:
    "Este caso segue para o Contábil e a empresa absorve o valor.",
  CONSEQUENCE_LOSS_WITHDRAWN:
    "O analista retirou a perda deste caso. Ao aprovar, nada é descontado do colaborador e nada segue para o Contábil.",
  CONSEQUENCE_REJECT:
    "O caso volta para o Analista ajustar. Nada é descontado do colaborador e nada segue para o Contábil.",
  CONFIRM_APPROVE: "Sim, aprovar",
  CONFIRM_REJECT: "Sim, reprovar",
  CANCEL: "Cancelar",
  PROCESSING: "Processando...",
  SUCCESS_APPROVE: "Divergência aprovada com sucesso",
  SUCCESS_REJECT: "Divergência reprovada com sucesso",
  ERROR: "Erro ao atualizar o status da divergência",
  REASON_LABEL: "Motivo da reprovação",
  REASON_PLACEHOLDER: "Explique por que este caso volta para o Analista",
  REASON_HINT: `Entre ${DECISION_REASON_MIN_LENGTH} e ${DECISION_REASON_MAX_LENGTH} caracteres. Vai para a trilha de auditoria e para o histórico do caso.`,
  REASON_TOO_SHORT: `Descreva o motivo com pelo menos ${DECISION_REASON_MIN_LENGTH} caracteres`,
} as const;

interface DecisionWording {
  readonly verb: string;
  readonly infinitive: string;
  readonly participleSingular: string;
  readonly participlePlural: string;
}

export const DECISION_VOCABULARY: Record<DecisionAction, DecisionWording> = {
  approve: {
    verb: DECISION_LABELS.APPROVE,
    infinitive: "aprovar",
    participleSingular: "aprovado",
    participlePlural: "aprovados",
  },
  reject: {
    verb: DECISION_LABELS.REJECT,
    infinitive: "reprovar",
    participleSingular: "reprovado",
    participlePlural: "reprovados",
  },
};

export const BATCH_ACTION_SCOPE = {
  SELECTION: "Selecionados",
  MODAL: "em Lote",
} as const;

interface BatchTrackLabels {
  readonly SECTION_TITLE: string;
  readonly SECTION_HINT: string;
  readonly SUMMARY: (count: number) => string;
}

export const BATCH_TRACK_LABELS: Record<DecisionTrack, BatchTrackLabels> = {
  [DECISION_TRACKS.PAYROLL_DISCOUNT]: {
    SECTION_TITLE: "Desconto em Folha — descontado do colaborador",
    SECTION_HINT:
      "Confira o colaborador e defina a quantidade de parcelas de cada caso.",
    SUMMARY: (count: number): string =>
      count === 1 ? "1 vai para o RH" : `${count} vão para o RH`,
  },
  [DECISION_TRACKS.ACCOUNTED_LOSS]: {
    SECTION_TITLE: "Contabiliza Perda — a empresa absorve o valor",
    SECTION_HINT:
      "Estes casos seguem para o Contábil. Não há colaborador a conferir nem parcelamento.",
    SUMMARY: (count: number): string =>
      count === 1
        ? "1 segue para o Contábil"
        : `${count} seguem para o Contábil`,
  },
  [DECISION_TRACKS.LOSS_WITHDRAWN]: {
    SECTION_TITLE: "Perda retirada — o analista desmarcou a perda",
    SECTION_HINT:
      "Nada é descontado do colaborador e nada segue para o Contábil. Não há colaborador a conferir nem parcelamento.",
    SUMMARY: (count: number): string =>
      count === 1
        ? "1 perda retirada"
        : `${count} perdas retiradas`,
  },
};

export const BATCH_APPROVAL_LABELS = {
  TITLE: "Aprovar em lote",
  INTRO: (total: number): string =>
    total === 1
      ? "1 caso selecionado. Confira o colaborador e aprove."
      : `${total} casos selecionados. Confira o colaborador de cada um e aprove.`,
  SPLIT_INTRO: (total: number, summaries: readonly string[]): string =>
    `${total} casos selecionados: ${summaries.join(" · ")}.`,
  LOADING: "Consultando o cadastro dos colaboradores...",
  ACK_ALL: "Concordo com a regra em todos os casos",
  DETAILS_SHOW: "Ver dados do colaborador",
  DETAILS_HIDE: "Ocultar dados",
  REPLACE: "Não é este colaborador",
  UNRESOLVED: "O sistema não identificou de quem descontar.",
  READY: (ready: number, total: number): string =>
    `${ready} de ${total} prontos para aprovar`,
  CONFIRM: (ready: number): string => `Aprovar (${ready})`,
  CANCEL: "Cancelar",
  BLOCKED: (count: number): string =>
    count === 1
      ? "1 caso selecionado não podia ser aprovado e ficou de fora"
      : `${count} casos selecionados não podiam ser aprovados e ficaram de fora`,
  PARTIAL: (pending: number): string =>
    pending === 1
      ? "1 caso continua pendente e não será aprovado agora."
      : `${pending} casos continuam pendentes e não serão aprovados agora.`,
} as const;

export const IDENTITY_QUEUE_LABELS = {
  TITLE: "Aprovar em lote",
  PROGRESS: (position: number, total: number): string =>
    `${position} de ${total}`,
  INTRO: (total: number): string =>
    total === 1
      ? "1 caso selecionado. Confira o colaborador e aprove."
      : `${total} casos selecionados. Confira o colaborador de cada um e aprove, um por vez.`,
  CASE: (id: number): string => `Divergência #${id}`,
  STORE: "Loja",
  DATE: "Data",
  SKIP: "Pular este",
  DONE_TITLE: "Fila encerrada",
  DONE_RESOLVED: (count: number): string =>
    count === 1 ? "1 caso identificado e aprovado" : `${count} casos identificados e aprovados`,
  DONE_SKIPPED: (count: number): string =>
    count === 1
      ? "1 caso continua aguardando decisão na lista"
      : `${count} casos continuam aguardando decisão na lista`,
  DONE_BLOCKED: (count: number): string =>
    count === 1
      ? "1 caso selecionado não podia ser aprovado e ficou de fora"
      : `${count} casos selecionados não podiam ser aprovados e ficaram de fora`,
  CLOSE: "Fechar",
} as const;

export const CLASSIFICATION_LABELS = {
  COLUMN_TITLE: "Se contabiliza",
  PAYROLL_DISCOUNT: "Desconto em Folha",
  ACCOUNTED_LOSS: "Contabiliza Perda",
  NOT_CLASSIFIED: "—",
} as const;

export const PAYROLL_VOUCHER_RESPONSE_LABELS = {
  COLUMN_TITLE: "Status RH",
  NOT_APPLICABLE: "—",
  NOT_SENT: "Não enviado",
  OPEN_RESPONSE: "Ver resposta do RH",
  DECIDED_BY: "Respondido por:",
  DECIDED_AT: "Data/Hora:",
  INSTALLMENTS: "Parcelamento",
  INSTALLMENT: "Parcela",
  AMOUNT: "Valor",
  TOTAL: "Total",
  REJECTION_REASON: "Motivo da reprovação",
  MODAL_TITLE: "Resposta do RH",
  MODAL_DESCRIPTION: "Decisão registrada pelo RH para este vale",
  CLOSE: "Fechar",
} as const;

export const DIVERGENCE_STATUS_LABELS = {
  PENDING: "Aguardando",
  ACTIVE: "Ativo",
  APPROVED: "Aprovado",
  REJECTED: "Reprovado",
  CLOSED: "Fechado",
  INACTIVE: "Inativo",
} as const;

export const RESUBMISSION_LABELS = {
  BADGE: "Reenvio",
  FILTER_OPTION: "Reenvio",
  BADGE_TITLE:
    "Item já aprovado que mudou depois e voltou para a sua decisão",
} as const;

export const CONCILIATION_GROUP_LABELS = {
  COLUMN_TITLE: "Conciliação",
  BADGE: (groupId: number): string => `Grupo ${groupId}`,
  BADGE_TITLE: (groupId: number): string =>
    `Conciliada entre caixas no grupo ${groupId}`,
  DERIVED_BADGE: "Derivada",
  DERIVED_TITLE: "Linha criada pela conciliação entre caixas",
} as const;

export const CONCILIATION_LINK_LABELS = {
  GO_TO: "Ir para a conciliação",
  TITLE: (conciliationId: number | string): string =>
    `Abrir a conciliação de caixa da loja (nº ${conciliationId}) — você sai desta tela`,
} as const;
